import type { ReactNode } from "react";
import type { TranslationKey } from "@privacy/translations";
import { useTranslation } from "@privacy/translations";
import { useSearchParams } from "react-router";
import autoAnimate from "@formkit/auto-animate";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { DynamicGrid } from "../dynamicGrid";
import { Drawer } from "../shared/Drawer";
import { EmptyState } from "../shared/EmptyState";
import { PAGE_PADDING_Y, PageSpinner, PageTitle, PageWrapper } from "../shared/Page";
import { customScrollbar } from "../shared/customScrollbar";
import { usePrivacyDocumentTitle } from "../shared/usePrivacyDocumentTitle";
import { OptionCategoryFormDialog } from "./OptionCategoryFormDialog";
import { OptionCategoryRow } from "./OptionCategoryRow";
import type { OptionCategoryFormValues } from "../api/optionCategories";
import type { ConnectedEntityFormType, OptionCategoryListItem, OptionCategoryListTextKeys } from "./types";

type OptionCategoryListPageProps = {
  textKeys: OptionCategoryListTextKeys;
  hasDescription: boolean;
  items: Array<OptionCategoryListItem> | undefined;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  onCreate: (values: OptionCategoryFormValues) => Promise<unknown>;
  onUpdate: (id: string, values: OptionCategoryFormValues) => Promise<unknown>;
  onDelete: (id: string) => Promise<unknown>;
  usageLinkTo?: (formType: ConnectedEntityFormType, optionValue: string) => string;
  historyTab: { listTabKey: TranslationKey; content: ReactNode; onHover: () => void };
};

export const OptionCategoryListPage = ({
  textKeys,
  hasDescription,
  items,
  canCreate,
  canUpdate,
  canDelete,
  onCreate,
  onUpdate,
  onDelete,
  usageLinkTo,
  historyTab,
}: OptionCategoryListPageProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  usePrivacyDocumentTitle(textKeys.title);

  if (!items) return <PageSpinner />;

  const isHistoryTabActive = searchParams.get("tab") === HISTORY_TAB;
  const sortedItems = [...items].sort((a, b) => a.label.localeCompare(b.label));

  return (
    <PageWrapper sx={{ height: "100vh", display: "flex", flexDirection: "column", pb: 0 }}>
      <Stack sx={{ maxWidth: LIST_MAX_WIDTH, gap: 3, flex: 1, minHeight: 0 }}>
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 3 }}
        >
          <PageTitle title={t(textKeys.title)} subTitle={t(textKeys.subTitle)} wrap />
          {canCreate && (
            <DynamicGrid.CreateNewButton
              labelKey={textKeys.createButton}
              renderDialog={({ onClose }) => (
                <OptionCategoryFormDialog
                  mode="create"
                  textKeys={textKeys}
                  hasDescription={hasDescription}
                  defaultValues={{ label: "", description: "" }}
                  onSubmit={onCreate}
                  onClose={onClose}
                />
              )}
            />
          )}
        </Stack>

        <Stack sx={{ flex: 1, minHeight: 0 }}>
          <Box sx={{ borderBottom: theme => `1px solid ${theme.palette.divider}` }}>
            <Drawer.TabButtons>
              <Drawer.TabButton active={!isHistoryTabActive} to={{ search: "" }}>
                {t(historyTab.listTabKey)}
              </Drawer.TabButton>
              <Drawer.TabButton
                active={isHistoryTabActive}
                to={{ search: `?tab=${HISTORY_TAB}` }}
                onHover={historyTab.onHover}
              >
                {t("Dynamic.Drawer.HistoryButton")}
              </Drawer.TabButton>
            </Drawer.TabButtons>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              scrollbarGutter: "stable",
              pb: PAGE_PADDING_Y,
              ...customScrollbar,
            }}
          >
            {isHistoryTabActive ? (
              <Box sx={{ pr: { xs: 1, sm: 4 } }}>{historyTab.content}</Box>
            ) : !sortedItems.length ? (
              <Box sx={{ height: "100%" }}>
                <EmptyState labelKey={textKeys.empty} />
              </Box>
            ) : (
              <Stack ref={animateListChanges} sx={{ pt: 2 }}>
                {sortedItems.map(item => (
                  <OptionCategoryRow
                    key={item.id}
                    item={item}
                    canUpdate={canUpdate}
                    canDelete={canDelete}
                    textKeys={textKeys}
                    hasDescription={hasDescription}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                    usageLinkTo={usageLinkTo}
                  />
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      </Stack>
    </PageWrapper>
  );
};

const animateListChanges = (node: HTMLElement | null) => (node ? autoAnimate(node).destroy : undefined);

const HISTORY_TAB = "history";
const LIST_MAX_WIDTH = 960;
